// src/components/Footer.js
import React from 'react';

const Footer = ({ 
  primaryColor,
  username = 'CodeKhmerRent',
  language = 'en' // 'en' or 'km'
}) => {
  const currentYear = new Date().getFullYear();

  // Translations
  const translations = {
    en: {
      about: 'About',
      aboutText: 'Your trusted e-commerce platform for quality products and exceptional service. Shop with confidence and support local businesses.',
      quickLinks: 'Quick Links',
      home: 'Home',
      products: 'Products',
      contact: 'Contact',
      contactInfo: 'Contact Info',
      followUs: 'Follow Us',
      allRightsReserved: 'All rights reserved.',
      poweredBy: 'Powered by',
      platform: 'Platform',
      email: 'info@codekhmerrent.com',
      phone: '+855 12 345 678',
      address: 'Phnom Penh, Cambodia'
    },
    km: {
      about: 'អំពីយើង',
      aboutText: 'វេទិកាពាណិជ្ជកម្មអេឡិចត្រូនិកដែលអ្នកទុកចិត្តសម្រាប់ផលិតផលដែលមានគុណភាព និងសេវាកម្មពិសេស។ ទិញដោយទំនុកចិត្ត និងគាំទ្រអាជីវកម្មក្នុងស្រុក។',
      quickLinks: 'តំណភ្ជាប់រហ័ស',
      home: 'ទំព័រដើម',
      products: 'ផលិតផល',
      contact: 'ទំនាក់ទំនង',
      contactInfo: 'ព័ត៌មានទំនាក់ទំនង',
      followUs: 'តាមដានពួកយើង',
      allRightsReserved: 'រក្សាសិទ្ធិគ្រប់យ៉ាង។',
      poweredBy: 'ដំណើរការដោយ',
      platform: 'វេទិកា',
      email: 'info@codekhmerrent.com',
      phone: '+855 12 345 678',
      address: 'ភ្នំពេញ, កម្ពុជា'
    }
  };

  const t = translations[language] || translations.en;

  // Get font family based on language
  const getFontFamily = () => {
    if (language === 'km') {
      return "'Kantumruy Pro', 'Noto Sans Khmer', 'Noto Serif Khmer', 'Khmer OS', sans-serif";
    }
    return "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  };

  // Social media links
  const socialLinks = [
    { platform: 'facebook', icon: 'fab fa-facebook', url: '#' },
    { platform: 'instagram', icon: 'fab fa-instagram', url: '#' },
    { platform: 'telegram', icon: 'fab fa-telegram', url: '#' },
    { platform: 'tiktok', icon: 'fab fa-tiktok', url: '#' },
    { platform: 'youtube', icon: 'fab fa-youtube', url: '#' }
  ];

  return (
    <footer 
      className="bg-gradient-to-b from-gray-900 to-gray-950 text-gray-300 py-12 md:py-16 mt-8"
      style={{ fontFamily: getFontFamily() }}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 mb-10">
          {/* About Section */}
          <div className="sm:col-span-2 lg:col-span-1">
            <h3 
              className="text-white font-semibold text-lg mb-4 tracking-tight"
              style={{ fontFamily: getFontFamily() }}
            >
              {t.about} {username}
            </h3>
            <p 
              className="text-gray-400 text-sm leading-relaxed"
              style={{ fontFamily: getFontFamily() }}
            >
              {t.aboutText}
            </p>
            {/* Brand Badge */}
            <div className="mt-4">
              <span 
                className="inline-block px-3 py-1 text-xs font-medium rounded-full"
                style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
              >
                <i className="fas fa-check-circle mr-1"></i>
                {language === 'km' ? 'គុណភាពធានា' : 'Quality Guaranteed'}
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 
              className="text-white font-semibold text-lg mb-4 tracking-tight"
              style={{ fontFamily: getFontFamily() }}
            >
              {t.quickLinks}
            </h3>
            <ul className="space-y-2.5">
              <li>
                <a 
                  href="#home" 
                  className="text-gray-400 hover:text-white text-sm transition-all duration-200 hover:pl-1 inline-block"
                  style={{ fontFamily: getFontFamily() }}
                >
                  <i className="fas fa-chevron-right text-[10px] mr-2 opacity-50"></i>
                  {t.home}
                </a>
              </li>
              <li>
                <a 
                  href="#products" 
                  className="text-gray-400 hover:text-white text-sm transition-all duration-200 hover:pl-1 inline-block"
                  style={{ fontFamily: getFontFamily() }}
                >
                  <i className="fas fa-chevron-right text-[10px] mr-2 opacity-50"></i>
                  {t.products}
                </a>
              </li>
              <li>
                <a 
                  href="#contact" 
                  className="text-gray-400 hover:text-white text-sm transition-all duration-200 hover:pl-1 inline-block"
                  style={{ fontFamily: getFontFamily() }}
                >
                  <i className="fas fa-chevron-right text-[10px] mr-2 opacity-50"></i>
                  {t.contact}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 
              className="text-white font-semibold text-lg mb-4 tracking-tight"
              style={{ fontFamily: getFontFamily() }}
            >
              {t.contactInfo}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-gray-400 text-sm group">
                <i className="fas fa-envelope text-xs mt-0.5 text-gray-500 group-hover:text-white transition-colors"></i>
                <span style={{ fontFamily: getFontFamily() }}>{t.email}</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm group">
                <i className="fas fa-phone text-xs mt-0.5 text-gray-500 group-hover:text-white transition-colors"></i>
                <span style={{ fontFamily: getFontFamily() }}>{t.phone}</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm group">
                <i className="fas fa-map-marker-alt text-xs mt-0.5 text-gray-500 group-hover:text-white transition-colors"></i>
                <span style={{ fontFamily: getFontFamily() }}>{t.address}</span>
              </li>
            </ul>
          </div>

          {/* Follow Us */}
          <div>
            <h3 
              className="text-white font-semibold text-lg mb-4 tracking-tight"
              style={{ fontFamily: getFontFamily() }}
            >
              {t.followUs}
            </h3>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((social, index) => (
                <a 
                  key={index}
                  href={social.url} 
                  className="w-10 h-10 rounded-full bg-gray-800/50 hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-white transition-all duration-300 hover:scale-110 hover:shadow-lg"
                  aria-label={social.platform}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className={`${social.icon} text-base`}></i>
                </a>
              ))}
            </div>
            {/* Newsletter Signup (optional) */}
            <div className="mt-4">
              <p className="text-gray-500 text-xs mb-2" style={{ fontFamily: getFontFamily() }}>
                {language === 'km' ? 'តាមដានពួកយើងសម្រាប់ការផ្តល់ជូនពិសេស' : 'Follow us for special offers'}
              </p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-800/60 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
              <p 
                className="text-gray-400 text-sm"
                style={{ fontFamily: getFontFamily() }}
              >
                &copy; {currentYear} {username}. {t.allRightsReserved}
              </p>
              <span className="hidden sm:inline text-gray-700 text-xs">|</span>
              <p 
                className="text-gray-500 text-xs flex items-center gap-1"
                style={{ fontFamily: getFontFamily() }}
              >
                {t.poweredBy} 
                <span style={{ color: primaryColor }} className="font-semibold">
                  {username} {t.platform}
                </span>
              </p>
            </div>
            
            {/* Back to Top Button */}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-2 text-gray-500 hover:text-white text-xs transition-all duration-200 hover:gap-3"
              style={{ fontFamily: getFontFamily() }}
            >
              <span>{language === 'km' ? 'ត្រឡប់ទៅកំពូល' : 'Back to Top'}</span>
              <i className="fas fa-arrow-up text-[10px]"></i>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;